import React, { useState } from "react";
import { Box, Button, Divider, InputAdornment, Popover, Stack, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useStocks } from "../../hooks/useStocks";
import StockAlmacenes from "./StockAlmacenes";
import UbicacionArticulo from "./UbicacionArticulo";

// A partir de aquí la lista ya no cabe en el panel sin desplazarse
const MIN_ARTICULOS_FILTRO = 8;

const coincide = (linea, texto) =>
    [linea.referencia, linea.nombre, linea.ean].some((campo) => String(campo ?? "").toLowerCase().includes(texto));

/**
 * Stock actual por nave y ubicación de cada artículo del inventario, con filtro si son muchos
 */
const ListaStockArticulos = ({ lineas }) => {
    const { stocks, ubicaciones, loading } = useStocks(lineas);
    const [filtro, setFiltro] = useState("");

    if (!lineas?.length) {
        return <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Sin artículos</Typography>;
    }

    const texto = filtro.trim().toLowerCase();
    const visibles = texto ? lineas.filter((linea) => coincide(linea, texto)) : lineas;
    const consultados = lineas.filter((linea) => linea.referencia in stocks).length;

    return (
        <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#212529" }}>
                {lineas.length} artículo{lineas.length === 1 ? "" : "s"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                {loading ? `Consultando stock... ${consultados} de ${lineas.length}` : "Stock actual por nave"}
            </Typography>

            {lineas.length > MIN_ARTICULOS_FILTRO && (
                <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder="Filtrar por referencia, nombre o EAN..."
                    value={filtro}
                    onChange={(evento) => setFiltro(evento.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "#6c757d" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 1.5 }}
                />
            )}

            {visibles.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Ningún artículo coincide con el filtro</Typography>
            ) : (
                <Stack spacing={1.25} divider={<Divider flexItem />}>
                    {visibles.map((linea) => (
                        <Box key={linea.id ?? linea.referencia}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#212529" }}>{linea.referencia}</Typography>
                            <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 0.5 }}>{linea.nombre}</Typography>
                            <Box sx={{ mb: 0.5 }}>
                                <UbicacionArticulo ubicacion={ubicaciones[linea.referencia]} />
                            </Box>
                            <StockAlmacenes stockPorAlmacen={stocks[linea.referencia]} titulo={null} />
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

/**
 * Botón "Ver stock" del listado de inventarios: despliega el stock por nave de
 * sus artículos. La consulta se hace al abrirlo, no al pintar el listado, para no
 * lanzar una búsqueda por cada artículo de cada inventario de la tabla
 */
const StockInventario = ({ lineas }) => {
    const [ancla, setAncla] = useState(null);
    const abierto = Boolean(ancla);

    return (
        <>
            <Button
                size="small"
                onClick={(evento) => {
                    evento.stopPropagation();
                    setAncla(evento.currentTarget);
                }}
                endIcon={abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, color: "#495057", "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}
            >
                Ver stock
            </Button>
            <Popover
                open={abierto}
                anchorEl={ancla}
                onClose={() => setAncla(null)}
                onClick={(evento) => evento.stopPropagation()}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{ sx: { p: 2, width: 340, maxWidth: "calc(100vw - 32px)", maxHeight: 420, borderRadius: 2 } }}
            >
                {abierto && <ListaStockArticulos lineas={lineas} />}
            </Popover>
        </>
    );
};

export default StockInventario;
